import React from 'react'; // GV This import is to validate that we're on the right React at the end of the file
import type { TypeDrawerConfig } from 'geoview-core/core/stores/states/drawer-state';
import type { TypeNavBarButtonConfig } from 'geoview-core/api/plugin/navbar-plugin';
import { NavBarPlugin } from 'geoview-core/api/plugin/navbar-plugin';

import { logger } from 'geoview-core/core/utils/logger';

import schema from '../schema.json';
import defaultConfig from '../default-config-drawer.json';
import { createDrawerButtons } from './draw-navbar';

// Import css styles
import './measurement-styles.css';

/**
 * Drawer plugin.
 */
class DrawerPlugin extends NavBarPlugin {
  /**
   * Returns the package schema.
   *
   * @returns The schema for this package
   */
  override schema(): unknown {
    return schema;
  }

  /**
   * Returns the default config for this package.
   *
   * @returns The default config
   */
  override defaultConfig(): unknown {
    return defaultConfig;
  }

  /**
   * Overrides the default translations for the Plugin.
   *
   * @returns The translations object for the Plugin
   */
  override defaultTranslations(): Record<string, unknown> {
    return {
      en: {
        drawer: {
          title: 'Draw',
          stopDrawing: 'Stop',
          stopDrawingTooltip: 'Stop Drawing',
          toggleDrawing: 'Toggle Drawing',
          clear: 'Clear',
          clearTooltip: 'Clear the drawings',
          edit: 'Toggle editing',
          fillColour: 'Fill Colour',
          strokeColour: 'Stroke Colour',
          strokeWidth: 'Stroke Width',
          iconSize: 'Icon Size',
          geometryPicker: 'Change geometry type',
          geometryPickerPanel: 'Select a geometry',
          point: 'Point',
          text: 'Text',
          textFont: 'Font',
          textSize: 'Font Size',
          textColour: 'Text Colour',
          textHaloColour: 'Halo Colour',
          textHaloWidth: 'Halo Width',
          textBold: 'Bold',
          textItalic: 'Italic',
          textFormatting: 'Text formatting',
          linestring: 'Line',
          polygon: 'Polygon',
          rectangle: 'Rectangle',
          circle: 'Circle',
          star: 'Star',
          style: 'Change Style',
          toggleSnapping: 'Toggle snapping',
          toggleMeasurements: 'Toggle measurements',
          undoTooltip: 'Undo',
          redoTooltip: 'Redo',
          downloadTooltip: 'Download drawings',
          uploadTooltip: 'Upload drawings',
          colourPicker: 'Pick a colour',
          toggleShortcuts: 'Toggle keyboard shortcuts',
          shortcutsEnabled: 'Drawer shortcuts enabled',
        },
      },
      fr: {
        drawer: {
          title: 'Dessiner',
          stopDrawing: 'Arrêter',
          stopDrawingTooltip: 'Arrêter le dessin',
          toggleDrawing: 'Basculer le dessin',
          clear: 'Effacer',
          clearTooltip: 'Effacer les dessins',
          edit: "Basculer l'édition",
          fillColour: 'Couleur de remplissage',
          strokeColour: 'Couleur du contour',
          strokeWidth: 'Largeur du contour',
          iconSize: "Taille de l'icône",
          geometryPicker: 'Changer le type de géométrie',
          geometryPickerPanel: 'Sélectionnez une géométrie',
          point: 'Pointer',
          text: 'Texte',
          textFont: 'Police de caractères',
          textSize: 'Taille de la police',
          textColour: 'Couleur du texte',
          textHaloColour: 'Couleur du halo',
          textHaloWidth: 'Largeur du halo',
          textBold: 'Gras',
          textItalic: 'Italique',
          textFormatting: 'Formatage du texte',
          linestring: 'Ligne',
          polygon: 'Polygone',
          rectangle: 'Rectangle',
          circle: 'Cercle',
          star: 'Étoile',
          style: 'Changer de style',
          toggleSnapping: "Basculer l'accrochage",
          toggleMeasurements: 'Basculer les mesures',
          undoTooltip: 'Défaire',
          redoTooltip: 'Refaire',
          downloadTooltip: 'Télécharger les dessins',
          uploadTooltip: 'Téléverser les dessins',
          colourPicker: 'Choisissez une couleur',
          toggleShortcuts: 'Basculer les raccourcis clavier',
          shortcutsEnabled: 'Raccourcis du dessin activés',
        },
      },
    };
  }

  /**
   * Overrides the getConfig in order to return the right type.
   *
   * @returns The Drawer config
   */
  override getConfig(): TypeDrawerConfig {
    // Redirect
    return super.getConfig() as TypeDrawerConfig;
  }

  /**
   * Overrides the creation of the buttons components to create Buttons with their optional panels.
   *
   * @returns The buttons to be added to the drawer bar
   */
  override onCreateButtonConfigs(): Record<string, TypeNavBarButtonConfig> {
    // Create all drawer buttons
    logger.logInfo('Drawer Plugin - onAdd');

    // GV: WORK AROUND - buttonPanel - Pass the translate function to createDrawerButtons
    // RESOLVED: Translation issues fixed - NavBar/AppBar components now translate aria-label and tooltip at render time
    // TO.DOCONT: Evaluate if dynamic button registration needs refactoring (UI reactivity to button creation events via handleNavApiAddButtonPanel)
    // TO.DOCONT: Consider cleanup of nav-bar, app-bar and footer-bar API's
    return createDrawerButtons(this.mapViewer.mapId, this.getConfig());
  }
}

export default DrawerPlugin;

// GV This if condition took over 3 days to investigate. It was giving errors on the app.geo.ca website with
// GV some conflicting reacts being loaded on the page for some obscure reason.
// Check if we're on the right react
if (React === window.cgpv.reactUtilities.react) {
  // Keep a reference to the Drawer Plugin as part of the geoviewPlugins property stored in the window object
  window.geoviewPlugins = window.geoviewPlugins || {};
  window.geoviewPlugins.drawer = DrawerPlugin;
}
